import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const HistorialPagosCuentaCorriente = ({ cliente, onClose }) => {
  const { token } = useAuth();
  const [historial, setHistorial] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [paginacion, setPaginacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paginaActual, setPaginaActual] = useState(1);
  const [filtroFormaPago, setFiltroFormaPago] = useState("");
  const [mostrarResumen, setMostrarResumen] = useState(true);

  // Cargar historial de pagos
  const cargarHistorial = async (pagina = 1) => {
    try {
      setLoading(true);
      setError("");
      
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/cuentas-corriente/cliente/${cliente.id}/historial`,
        {
          params: { page: pagina, limit: 15 },
          headers: { 'x-auth-token': token }
        }
      );

      setHistorial(response.data.historial);
      setResumen(response.data.resumen);
      setPaginacion(response.data.paginacion);
      setPaginaActual(pagina);
      
    } catch (err) {
      console.error('Error al cargar historial:', err);
      const errorMsg = err.response?.data?.error || 'Error al cargar el historial de pagos';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cliente?.id) {
      cargarHistorial(1);
    }
  }, [cliente?.id, token]);

  // Filtrar historial por forma de pago
  const historialFiltrado = filtroFormaPago 
    ? historial.filter(pago => pago.forma_pago === filtroFormaPago)
    : historial;

  // Obtener formas de pago únicas para el filtro
  const formasPagoUnicas = [...new Set(historial.map(pago => pago.forma_pago))];

  // Formatear fecha
  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener color para forma de pago
  const getColorFormaPago = (formaPago) => {
    const colores = {
      efectivo: 'bg-green-100 text-green-800',
      transferencia: 'bg-blue-100 text-blue-800',
      tarjeta: 'bg-purple-100 text-purple-800',
      otros: 'bg-orange-100 text-orange-800',
      cuentaCorriente: 'bg-gray-100 text-gray-800'
    };
    return colores[formaPago] || 'bg-gray-100 text-gray-800';
  };

  // Función para imprimir ticket de pago individual
  const imprimirTicketPago = (pago, cliente) => {
    const fechaActual = new Date().toLocaleString('es-AR');
    
    const contenidoTicket = `
      <div style="width: 300px; font-family: 'Courier New', monospace; font-size: 12px; padding: 10px; line-height: 1.3;">
        <div style="text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h2 style="margin: 0; font-size: 18px; font-weight: bold;">ZAFPAN</h2>
          <p style="margin: 3px 0; font-size: 12px;">Comprobante de Pago</p>
          <p style="margin: 0; font-size: 10px;">${fechaActual}</p>
        </div>
        
        <div style="margin-bottom: 12px;">
          <p style="margin: 2px 0;"><strong>Cliente:</strong> ${cliente.nombre}</p>
          ${cliente.DNI ? `<p style="margin: 2px 0;"><strong>DNI:</strong> ${cliente.DNI}</p>` : ''}
          ${cliente.telefono ? `<p style="margin: 2px 0;"><strong>Tel:</strong> ${cliente.telefono}</p>` : ''}
        </div>

        <div style="border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-align: center; font-weight: bold;">DETALLE DEL PAGO</h3>
          
          <div style="margin-bottom: 8px; padding: 6px; border: 2px solid #333;">
            <p style="margin: 2px 0; font-weight: bold; text-align: center;">Pago ID #${pago.id}</p>
            <p style="margin: 2px 0;"><strong>Fecha Pago:</strong> ${formatearFecha(pago.fecha_pago)}</p>
            <p style="margin: 2px 0;"><strong>Monto Pagado:</strong> $${pago.monto_pagado.toFixed(2)}</p>
            <p style="margin: 2px 0;"><strong>Forma de Pago:</strong> ${pago.forma_pago.toUpperCase()}</p>
          </div>

          <div style="margin-bottom: 8px; padding: 6px; border: 1px solid #666;">
            <p style="margin: 2px 0; font-weight: bold;">INFORMACIÓN DE LA CUENTA:</p>
            <p style="margin: 1px 0;">Venta Original: #${pago.cuenta_corriente.id_venta}</p>
            <p style="margin: 1px 0;">Total Original: $${pago.cuenta_corriente.total_original.toFixed(2)}</p>
            <p style="margin: 1px 0;">Saldo Actual: $${pago.cuenta_corriente.saldo_actual.toFixed(2)}</p>
            ${pago.cuenta_corriente.fecha_venta ? `<p style="margin: 1px 0; font-size: 10px;">Fecha Venta: ${formatearFecha(pago.cuenta_corriente.fecha_venta)}</p>` : ''}
          </div>
        </div>

        <div style="text-align: center; font-size: 16px; margin: 12px 0; border: 2px solid #000; padding: 8px;">
          <p style="margin: 0; font-weight: bold;">ESTADO DE CUENTA</p>
          ${pago.cuenta_corriente.saldo_actual === 0 ? 
            `<p style="margin: 2px 0; font-size: 18px; font-weight: bold; color: green;">✅ CUENTA LIQUIDADA</p>` :
            `<p style="margin: 2px 0; font-size: 16px; font-weight: bold;">SALDO PENDIENTE: $${pago.cuenta_corriente.saldo_actual.toFixed(2)}</p>`
          }
        </div>

        <div style="border-top: 2px dashed #000; padding-top: 8px; text-align: center; font-size: 10px;">
          <p style="margin: 1px 0;">Gracias por su pago</p>
          <p style="margin: 1px 0;">Conserve este comprobante</p>
          <p style="margin: 1px 0;">Ticket generado: ${fechaActual}</p>
        </div>
      </div>
    `;

    // Crear ventana de impresión
    const ventanaImpresion = window.open('', '_blank', 'width=300,height=600');
    ventanaImpresion.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Comprobante de Pago</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              font-size: 12px;
              background: white;
              padding: 0;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
              @page { 
                size: 80mm auto; 
                margin: 0; 
              }
            }
          </style>
        </head>
        <body>
          ${contenidoTicket}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    ventanaImpresion.document.close();

    toast.success(`Comprobante de pago #${pago.id} enviado a impresión`);
  };

  // Función para imprimir resumen completo del historial
  const imprimirResumenHistorial = (cliente, historialFiltrado, resumen) => {
    const fechaActual = new Date().toLocaleString('es-AR');
    
    const contenidoTicket = `
      <div style="width: 300px; font-family: 'Courier New', monospace; font-size: 12px; padding: 10px; line-height: 1.3;">
        <div style="text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h2 style="margin: 0; font-size: 18px; font-weight: bold;">ZAFPAN</h2>
          <p style="margin: 3px 0; font-size: 12px;">Historial de Pagos</p>
          <p style="margin: 0; font-size: 10px;">${fechaActual}</p>
        </div>
        
        <div style="margin-bottom: 12px;">
          <p style="margin: 2px 0;"><strong>Cliente:</strong> ${cliente.nombre}</p>
          ${cliente.DNI ? `<p style="margin: 2px 0;"><strong>DNI:</strong> ${cliente.DNI}</p>` : ''}
          ${cliente.telefono ? `<p style="margin: 2px 0;"><strong>Tel:</strong> ${cliente.telefono}</p>` : ''}
        </div>

        <div style="border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-align: center; font-weight: bold;">RESUMEN ESTADÍSTICO</h3>
          
          <div style="margin-bottom: 8px; padding: 6px; border: 2px solid #333;">
            <p style="margin: 2px 0; font-weight: bold; text-align: center;">TOTALES</p>
            <p style="margin: 1px 0;">Total Pagado: $${resumen.total_pagado.toFixed(2)}</p>
            <p style="margin: 1px 0;">Cantidad de Pagos: ${resumen.total_pagos}</p>
            <p style="margin: 1px 0;">Promedio por Pago: $${(resumen.total_pagado / resumen.total_pagos).toFixed(2)}</p>
          </div>

          <div style="margin-bottom: 8px; padding: 6px; border: 1px solid #666;">
            <p style="margin: 2px 0; font-weight: bold;">POR FORMA DE PAGO:</p>
            ${resumen.por_forma_pago.map(item => `
              <p style="margin: 1px 0; font-size: 10px;">${item.forma_pago.toUpperCase()}: $${item.total_monto.toFixed(2)}</p>
            `).join('')}
          </div>
        </div>

        <div style="border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 10px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-align: center; font-weight: bold;">ÚLTIMOS PAGOS</h3>
          ${historialFiltrado.slice(0, 5).map(pago => `
            <div style="margin-bottom: 6px; padding: 4px; border: 1px solid #ccc;">
              <p style="margin: 1px 0; font-size: 10px;"><strong>Pago #${pago.id}</strong></p>
              <p style="margin: 1px 0; font-size: 10px;">Fecha: ${formatearFecha(pago.fecha_pago)}</p>
              <p style="margin: 1px 0; font-size: 10px;">Monto: $${pago.monto_pagado.toFixed(2)} (${pago.forma_pago})</p>
              <p style="margin: 1px 0; font-size: 10px;">Venta: #${pago.cuenta_corriente.id_venta}</p>
            </div>
          `).join('')}
          ${historialFiltrado.length > 5 ? `
            <p style="margin: 4px 0; font-size: 10px; text-align: center; font-style: italic;">
              ... y ${historialFiltrado.length - 5} pagos más
            </p>
          ` : ''}
        </div>

        <div style="text-align: center; font-size: 16px; margin: 12px 0; border: 2px solid #000; padding: 8px;">
          <p style="margin: 0; font-weight: bold;">TOTAL HISTÓRICO</p>
          <p style="margin: 2px 0; font-size: 20px; font-weight: bold;">$${resumen.total_pagado.toFixed(2)}</p>
          <p style="margin: 0; font-size: 12px;">${resumen.total_pagos} pagos realizados</p>
        </div>

        <div style="border-top: 2px dashed #000; padding-top: 8px; text-align: center; font-size: 10px;">
          <p style="margin: 1px 0;">Gracias por su confianza</p>
          <p style="margin: 1px 0;">Conserve este resumen</p>
          <p style="margin: 1px 0;">Ticket generado: ${fechaActual}</p>
        </div>
      </div>
    `;

    // Crear ventana de impresión
    const ventanaImpresion = window.open('', '_blank', 'width=300,height=700');
    ventanaImpresion.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Historial de Pagos</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              font-size: 12px;
              background: white;
              padding: 0;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
              @page { 
                size: 80mm auto; 
                margin: 0; 
              }
            }
          </style>
        </head>
        <body>
          ${contenidoTicket}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    ventanaImpresion.document.close();

    toast.success("Resumen de historial de pagos enviado a impresión");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="animate-pulse space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                <div>
                  <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 bg-gray-100 rounded-xl"></div>
                ))}
              </div>
              <div className="space-y-3 mt-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-100 rounded-xl"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-3">
      <div className="max-w-7xl mx-auto">
        {/* Header compacto */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-2 rounded-lg">
                <span className="text-white text-lg">📋</span>
              </div>
              <div>
                <h4 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Historial de Pagos
                </h4>
                <p className="text-sm text-gray-600">Cliente: <span className="font-semibold">{cliente.nombre}</span></p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-3 py-2 bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2 text-sm"
            >
              <span>←</span>
              Volver
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4 mb-6 text-red-700 flex items-center gap-3">
            <span className="text-2xl">❌</span>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Resumen estadístico compacto */}
        {resumen && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg">
                  <span className="text-white text-lg">📊</span>
                </div>
                <div>
                  <h5 className="text-lg font-bold text-gray-800">Resumen Estadístico</h5>
                  <p className="text-sm text-gray-600">Análisis de pagos del cliente</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => imprimirResumenHistorial(cliente, historialFiltrado, resumen)}
                  className="px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-sm flex items-center gap-1"
                  title="Imprimir resumen completo del historial"
                >
                  <span>🖨️</span>
                  <span>Imprimir</span>
                </button>
                <button
                  onClick={() => setMostrarResumen(!mostrarResumen)}
                  className="px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-sm"
                >
                  {mostrarResumen ? '🙈 Ocultar' : '👁️ Mostrar'}
                </button>
              </div>
            </div>
            
            <AnimatePresence>
              {mostrarResumen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-1 lg:grid-cols-3 gap-4"
                >
                  <div className="bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h6 className="font-bold text-green-800 mb-1 text-sm">💰 Total Pagado</h6>
                        <p className="text-2xl font-bold text-green-600">${resumen.total_pagado.toFixed(2)}</p>
                        <p className="text-xs text-green-700 mt-1">
                          📊 {resumen.total_pagos} pago{resumen.total_pagos !== 1 ? 's' : ''} realizado{resumen.total_pagos !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="bg-green-500 p-2 rounded-full">
                        <span className="text-white text-lg">💵</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h6 className="font-bold text-blue-800 mb-2 text-sm">💳 Por Forma de Pago</h6>
                        <div className="space-y-1">
                          {resumen.por_forma_pago.map(item => (
                            <div key={item.forma_pago} className="flex justify-between items-center bg-white p-1 rounded-lg border border-blue-100">
                              <span className="capitalize font-medium text-gray-700 flex items-center gap-1 text-xs">
                                {item.forma_pago === 'efectivo' && '💵'}
                                {item.forma_pago === 'transferencia' && '🏦'}
                                {item.forma_pago === 'tarjeta' && '💳'}
                                {item.forma_pago === 'otros' && '🏷️'}
                                {item.forma_pago === 'cuentaCorriente' && '📋'}
                                {item.forma_pago}
                              </span>
                              <span className="font-bold text-blue-600 text-xs">${item.total_monto.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-pink-100 border-2 border-purple-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h6 className="font-bold text-purple-800 mb-1 text-sm">📈 Promedio por Pago</h6>
                        <p className="text-2xl font-bold text-purple-600">
                          ${(resumen.total_pagado / resumen.total_pagos).toFixed(2)}
                        </p>
                        <p className="text-xs text-purple-700 mt-1">por transacción</p>
                      </div>
                      <div className="bg-purple-500 p-2 rounded-full">
                        <span className="text-white text-lg">📊</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Filtros compactos */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
          
            <div>
              <h5 className="text-lg font-bold text-gray-800">Filtros de Búsqueda</h5>
              <p className="text-sm text-gray-600">Personaliza tu búsqueda de pagos</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-48">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                💳 <span>Forma de Pago</span>
              </label>
              <select
                value={filtroFormaPago}
                onChange={e => setFiltroFormaPago(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 bg-gray-50 hover:bg-white text-sm"
              >
                <option value="">🌟 Todas las formas de pago</option>
                {formasPagoUnicas.map(forma => (
                  <option key={forma} value={forma} className="capitalize">
                    {forma === 'efectivo' && '💵'} 
                    {forma === 'transferencia' && '🏦'} 
                    {forma === 'tarjeta' && '💳'} 
                    {forma === 'otros' && '🏷️'} 
                    {forma === 'cuentaCorriente' && '📋'} 
                    {forma}
                  </option>
                ))}
              </select>
            </div>
            
            {filtroFormaPago && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-800 font-medium text-sm">📊 Mostrando {historialFiltrado.length} de {historial.length} pagos</span>
                  <button
                    onClick={() => setFiltroFormaPago('')}
                    className="ml-2 text-emerald-600 hover:text-emerald-800 bg-emerald-100 hover:bg-emerald-200 rounded-full w-5 h-5 flex items-center justify-center transition-all duration-200 text-xs"
                    title="Limpiar filtro"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lista de pagos mejorada */}
        {historialFiltrado.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-gray-400 text-4xl">💳</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              {filtroFormaPago ? 'Sin pagos con ese filtro' : 'No hay pagos registrados'}
            </h3>
            <p className="text-gray-600 mb-6">
              {filtroFormaPago 
                ? `No se encontraron pagos con la forma de pago "${filtroFormaPago}"` 
                : 'Este cliente aún no tiene pagos registrados en su cuenta corriente'
              }
            </p>
            {filtroFormaPago && (
              <button
                onClick={() => setFiltroFormaPago('')}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                🔄 Mostrar todos los pagos
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                💰 <span>Lista de Pagos ({historialFiltrado.length})</span>
              </h5>
              <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                Total: ${historialFiltrado.reduce((sum, pago) => sum + pago.monto_pagado, 0).toFixed(2)}
              </div>
            </div>
            
            <AnimatePresence>
              {historialFiltrado.map((pago, index) => (
                <motion.div
                  key={pago.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:border-blue-200 transition-all duration-200 transform hover:scale-[1.02]"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-md">
                          💵 ${pago.monto_pagado.toFixed(2)}
                        </div>
                        <span className={`px-4 py-2 rounded-xl text-sm font-bold shadow-md ${getColorFormaPago(pago.forma_pago)}`}>
                          {pago.forma_pago === 'efectivo' && '💵'} 
                          {pago.forma_pago === 'transferencia' && '🏦'} 
                          {pago.forma_pago === 'tarjeta' && '💳'} 
                          {pago.forma_pago === 'otros' && '🏷️'} 
                          {pago.forma_pago === 'cuentaCorriente' && '📋'} 
                          {pago.forma_pago.toUpperCase()}
                        </span>
                        <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium border border-blue-200">
                          📅 {formatearFecha(pago.fecha_pago)}
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div className="bg-white p-3 rounded-lg border border-gray-100">
                            <div className="font-semibold text-gray-700 mb-1">🛒 Venta</div>
                            <div className="text-blue-600 font-bold">#${pago.cuenta_corriente.id_venta}</div>
                          </div>
                          <div className="bg-white p-3 rounded-lg border border-gray-100">
                            <div className="font-semibold text-gray-700 mb-1">💰 Total Original</div>
                            <div className="text-orange-600 font-bold">${pago.cuenta_corriente.total_original.toFixed(2)}</div>
                          </div>
                          <div className="bg-white p-3 rounded-lg border border-gray-100">
                            <div className="font-semibold text-gray-700 mb-1">⚖️ Saldo Actual</div>
                            <div className={`font-bold ${pago.cuenta_corriente.saldo_actual === 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${pago.cuenta_corriente.saldo_actual.toFixed(2)}
                            </div>
                          </div>
                        </div>
                        
                        {pago.cuenta_corriente.fecha_venta && (
                          <div className="mt-3 text-xs text-gray-600 bg-blue-50 p-2 rounded-lg border border-blue-100">
                            📆 <strong>Fecha de venta:</strong> {formatearFecha(pago.cuenta_corriente.fecha_venta)}
                          </div>
                        )}
                        
                        {pago.cuenta_corriente.observaciones && (
                          <div className="mt-3 text-xs text-gray-600 bg-yellow-50 p-2 rounded-lg border border-yellow-200">
                            📝 <strong>Observaciones:</strong> {pago.cuenta_corriente.observaciones}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      <div className="flex flex-col gap-3">
                        {pago.cuenta_corriente.saldo_actual === 0 ? (
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-bold text-center shadow-lg">
                            <div className="text-2xl mb-1">✅</div>
                            <div className="text-sm">CUENTA</div>
                            <div className="text-sm">LIQUIDADA</div>
                          </div>
                        ) : (
                          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-bold text-center shadow-lg">
                            <div className="text-2xl mb-1">⏳</div>
                            <div className="text-sm">SALDO</div>
                            <div className="text-sm">PENDIENTE</div>
                          </div>
                        )}
                        
                        <button
                          onClick={() => imprimirTicketPago(pago, cliente)}
                          className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-4 py-2 rounded-xl font-bold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center gap-2 text-sm"
                          title={`Imprimir comprobante del pago #${pago.id}`}
                        >
                          <span>🎫</span>
                          <span>Imprimir</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Paginación modernizada */}
        {paginacion && paginacion.total_paginas > 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <button
                onClick={() => cargarHistorial(paginaActual - 1)}
                disabled={paginaActual <= 1}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:transform-none disabled:hover:shadow-md"
              >
                <span>⬅️</span>
                <span className="font-medium">Anterior</span>
              </button>
              
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-2">
                {[...Array(paginacion.total_paginas)].map((_, i) => {
                  const pagina = i + 1;
                  const esActual = pagina === paginaActual;
                  const mostrar = pagina === 1 || pagina === paginacion.total_paginas || 
                                Math.abs(pagina - paginaActual) <= 2;
                  
                  if (!mostrar) {
                    if (pagina === paginaActual - 3 || pagina === paginaActual + 3) {
                      return (
                        <span key={i} className="px-3 py-2 text-gray-400 font-bold">
                          ...
                        </span>
                      );
                    }
                    return null;
                  }
                  
                  return (
                    <button
                      key={i}
                      onClick={() => cargarHistorial(pagina)}
                      className={`min-w-12 h-12 rounded-xl font-bold transition-all duration-200 transform hover:scale-105 ${
                        esActual 
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg' 
                          : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      {pagina}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => cargarHistorial(paginaActual + 1)}
                disabled={paginaActual >= paginacion.total_paginas}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:transform-none disabled:hover:shadow-md"
              >
                <span className="font-medium">Siguiente</span>
                <span>➡️</span>
              </button>
            </div>
            
            <div className="mt-4 text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-4 py-2 rounded-xl text-sm font-medium border border-blue-200">
                <span>📄</span>
                <span>Página {paginaActual} de {paginacion.total_paginas}</span>
              </div>
            </div>
          </div>
        )}

        {/* Footer con información mejorado */}
        {paginacion && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 text-center border border-blue-200">
            <div className="flex items-center justify-center gap-2 text-blue-700 font-medium">
              <span className="text-lg">📊</span>
              <span>
                Mostrando <strong>{((paginaActual - 1) * paginacion.registros_por_pagina) + 1}</strong> - <strong>{Math.min(paginaActual * paginacion.registros_por_pagina, paginacion.total_registros)}</strong> de <strong>{paginacion.total_registros}</strong> pagos
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistorialPagosCuentaCorriente;
